import type { AdminCreateDropRequestInput, AdminUpdateDropRequest } from '@pulsify/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { dropsAdminApi } from '~/entities/drop/api/drops'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

export function useAdminDrops() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_DROPS),
    queryFn: () => dropsAdminApi.list(),
    enabled: !!session && storageManager.isAdmin(),
  })
}

export function useDropLinkSearch(kind: 'none' | 'event' | 'listing', query: string) {
  const term = query.trim()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_DROPS, 'link-search', kind, term),
    queryFn: () => dropsAdminApi.searchLinkTargets(kind as 'event' | 'listing', term),
    enabled: kind !== 'none' && term.length >= 2,
  })
}

export function useDailyDropStats(dropId: string | null) {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_DROPS, dropId, 'daily-stats'),
    queryFn: () => dropsAdminApi.dailyStats(dropId as string),
    enabled: !!session && storageManager.isAdmin() && !!dropId,
    refetchInterval: 30_000,
  })
}

export function useAdminDropEntries(dropId: string | null) {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_DROPS, dropId, 'entries'),
    queryFn: () => dropsAdminApi.entries(dropId as string),
    enabled: !!session && storageManager.isAdmin() && !!dropId,
  })
}

function useDropMutation<TInput>(
  mutationFn: (input: TInput) => Promise<unknown>,
  successMessage: () => string,
  logLabel: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_DROPS) })
      toast.success(successMessage())
    },
    onError: (error: unknown) => {
      log.error(error as Error, `[Drops] ${logLabel} failed`)
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useCreateDrop() {
  return useDropMutation(
    (input: AdminCreateDropRequestInput) => dropsAdminApi.create(input),
    m.dropCreated,
    'Create',
  )
}

export function useUpdateDrop() {
  return useDropMutation(
    ({ id, input }: { id: string; input: AdminUpdateDropRequest }) =>
      dropsAdminApi.update(id, input),
    m.dropUpdated,
    'Update',
  )
}

export function usePublishDrop() {
  return useDropMutation((id: string) => dropsAdminApi.publish(id), m.dropPublished, 'Publish')
}

export function useArchiveDrop() {
  return useDropMutation((id: string) => dropsAdminApi.archive(id), m.dropArchived, 'Archive')
}

export function useDrawDrop() {
  return useDropMutation((id: string) => dropsAdminApi.draw(id), m.dropDrawComplete, 'Draw')
}

export function useUploadDropHero() {
  return useDropMutation(
    ({ id, file }: { id: string; file: File }) => dropsAdminApi.uploadHero(id, file),
    m.dropHeroUploaded,
    'Hero upload',
  )
}

export function useMessageDropWinner() {
  return useDropMutation(
    ({ id, userId, message }: { id: string; userId: string; message: string }) =>
      dropsAdminApi.messageWinner(id, userId, message),
    m.dropMessageSent,
    'Winner message',
  )
}

export function useMessageDropWinners() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      message,
      file,
      resend,
    }: {
      id: string
      message: string
      file: File | null
      resend: boolean
    }) => {
      const attachment = file ? await dropsAdminApi.uploadWinnerAttachment(file) : undefined
      return dropsAdminApi.messageWinners(id, { message, attachment, resend: resend || undefined })
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_DROPS) })
      toast.success(m.dropWinnersSentToast({ count: result.sentCount }))
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Drops] Winners message failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}
